import React, { useState, useEffect } from 'react';
import { Search, Swords, Menu, X, ChevronDown, Lock, Loader, ExternalLink, Trophy, Archive, BarChart2, Terminal, Database, Users } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { subnetNames } from '../utils/subnetNames';
import { useWallet } from '../hooks/useWallet';
import { usePremiumStatus } from '../hooks/usePremiumStatus';
import { truncateAddress } from '../utils/chartHelpers';
import { UsernamePrompt } from './UsernamePrompt';
import { createUser } from '../services/userService';
import { ProfileButton } from './ProfileButton';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchInput, setSearchInput] = React.useState('');
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isSubnetsOpen, setIsSubnetsOpen] = React.useState(false);
  const [isBountiesOpen, setIsBountiesOpen] = React.useState(false);
  const { address, isConnected, connect, disconnect, needsUsername, isCheckingUser, userData } = useWallet();
  const { isPremium, loading: checkingPremium } = usePremiumStatus();
  const [usernameError, setUsernameError] = useState<string>();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim()) {
      navigate(`/wallet/${searchInput.trim()}`);
      setSearchInput('');
      setIsMenuOpen(false);
    }
  };

  const handleSubnetClick = (netuid: number) => {
    navigate(`/subnet/${netuid}`);
    setIsMenuOpen(false);
    setIsSubnetsOpen(false);
  };

  const handleUsernameSubmit = async (username: string) => {
    try {
      if (!address) return;
      await createUser(address, username);
    } catch (error) {
      if (error instanceof Error) {
        setUsernameError(error.message);
      } else {
        setUsernameError('Failed to set username. Please try again.');
      }
      throw error;
    }
  };

  const bountyPages = [
    { path: '/bounties', label: 'Active Bounties', icon: Trophy },
    { path: '/bounties/archive', label: 'Archive', icon: Archive },
    { path: '/bounties/analytics', label: 'Analytics', icon: BarChart2 },
    { path: '/terminal', label: 'Terminal', icon: Terminal }
  ];

  // Admin pages - only visible to specific addresses
  const isAdmin = address && ['0x1234567890abcdef1234567890abcdef12345678'].includes(address.toLowerCase());
  const adminPages = [
    { path: '/admin', label: 'Admin Panel', icon: Database }
  ];

  // Sort subnets by netuid
  const sortedSubnets = Object.entries(subnetNames)
    .map(([netuid, name]) => ({
      netuid: parseInt(netuid),
      name
    }))
    .sort((a, b) => a.netuid - b.netuid);

  // Create columns with 17 items each
  const columns = [];
  for (let i = 0; i < sortedSubnets.length; i += 17) {
    columns.push(sortedSubnets.slice(i, i + 17));
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black bg-[length:400%_400%] animate-[gradient_15s_ease_infinite] text-white">
      {needsUsername && (
        <UsernamePrompt
          onSubmit={handleUsernameSubmit}
          onClose={disconnect}
          error={usernameError}
        />
      )}

      <header className="sticky top-0 z-40 w-full">
        <div className="relative z-50 bg-black/40 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-4">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <Link to="/" className="flex items-center space-x-4">
                <div className="w-10 h-10 md:w-12 md:h-12 relative logo-container-main">
                  <img 
                    src="/Untitled design (5).png" 
                    alt="Logo" 
                    className="logo-3d-main w-full h-full rounded-full border-2 border-cyan-400"
                  />
                </div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                  <span className="text-cyan-400">SWORD</span>SCAN
                </h1>
              </Link>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden absolute right-4 top-4 p-2 hover:bg-black/20 rounded-lg transition-colors"
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>

              {/* Mobile menu */}
              <div className={`md:hidden w-full transition-all duration-300 ease-in-out ${
                isMenuOpen 
                  ? 'max-h-[500px] opacity-100' 
                  : 'max-h-0 opacity-0 overflow-hidden'
              }`}>
                <div className="space-y-4 py-2">
                  <form onSubmit={handleSubmit} className="w-full">
                    <div className="relative">
                      <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Enter Wallet Address"
                        className="w-full bg-black/20 border border-white/10 rounded-lg py-2 px-4 pl-10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm"
                      />
                      <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                    </div>
                  </form>

                  {/* Mobile Subnets Menu */}
                  <div className="relative">
                    <button
                      onClick={() => setIsSubnetsOpen(!isSubnetsOpen)}
                      className="w-full flex items-center justify-between bg-black/20 hover:bg-black/30 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      <span>Subnets</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isSubnetsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isSubnetsOpen && (
                      <div className="mt-2 py-2 bg-black/90 backdrop-blur-xl rounded-lg border border-white/20 max-h-[60vh] overflow-y-auto shadow-xl shadow-black/50">
                        <div className="grid grid-cols-1 gap-1">
                          {sortedSubnets.map(({ netuid, name }) => (
                            <button
                              key={netuid}
                              onClick={() => handleSubnetClick(netuid)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-white/20 transition-colors"
                            >
                              {netuid}: {name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* TensorPulse Mindshare Link */}
                  <Link
                    to="/tensorpulse-mindshare"
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center space-x-2 px-4 py-2 text-sm bg-black/20 hover:bg-black/30 rounded-lg ${
                      location.pathname === '/tensorpulse-mindshare' ? 'bg-white/10' : ''
                    }`}
                  >
                    <span>TensorPulse Mindshare</span>
                  </Link>

                  {/* KOLScan Link */}
                  <Link
                    to="/kolscan"
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center space-x-2 px-4 py-2 text-sm bg-black/20 hover:bg-black/30 rounded-lg ${
                      location.pathname === '/kolscan' ? 'bg-white/10' : ''
                    }`}
                  >
                    <Users className="w-4 h-4 text-cyan-400" />
                    <span>KOLScan</span>
                  </Link>

                  {/* Admin section - only visible to admin addresses */}
                  {isAdmin && (
                    <div className="pt-2 border-t border-white/10">
                      {adminPages.map(({ path, label, icon: Icon }) => (
                        <Link
                          key={path}
                          to={path}
                          onClick={() => setIsMenuOpen(false)}
                          className={`flex items-center space-x-2 px-4 py-2 text-sm bg-black/20 hover:bg-black/30 rounded-lg mb-2 ${
                            location.pathname === path ? 'bg-white/10' : ''
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{label}</span>
                        </Link>
                      ))}
                    </div>
                  )}

                  {isConnected && userData ? (
                    <ProfileButton
                      username={userData.username}
                      address={address!}
                      stakedAmount={userData.stakedAmount}
                      totalPoints={userData.totalPoints}
                      rank={userData.rank}
                      onDisconnect={disconnect}
                    />
                  ) : (
                    <button
                      onClick={connect}
                      disabled={isCheckingUser}
                      className="w-full flex items-center justify-between bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      <span>Connect Wallet</span>
                      {isCheckingUser && (
                        <Loader className="w-4 h-4 animate-spin" />
                      )}
                    </button>
                  )}

                  <div className="text-center">
                    <span className="text-[10px] text-gray-400">Beta v1.0</span>
                  </div>
                </div>
              </div>

              {/* Desktop menu */}
              <div className="hidden md:flex md:flex-1 md:items-center md:gap-4">
                <form onSubmit={handleSubmit} className="w-full md:flex-1 md:max-w-xl">
                  <div className="relative">
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Enter Wallet Address"
                      className="w-full bg-black/20 border border-white/10 rounded-lg py-2 px-4 pl-10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-sm md:text-base"
                    />
                    <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  </div>
                </form>

                <div className="flex items-center gap-2 md:gap-4">
                  {/* Desktop Subnets Menu */}
                  <div className="relative group">
                    <button className="flex items-center space-x-1 bg-black/20 hover:bg-black/30 text-white px-3 md:px-4 py-2 rounded-lg text-sm md:text-base font-medium transition-colors">
                      <span>Subnets</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <div className="fixed left-1/2 mt-2 w-[95vw] max-w-[1600px] py-8 bg-black/90 backdrop-blur-xl rounded-xl border border-white/20 shadow-2xl shadow-black/50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 max-h-[80vh] overflow-y-auto -translate-x-1/2">
                      <div className="grid grid-cols-6 gap-8 px-8">
                        {columns.map((column, columnIndex) => (
                          <div key={columnIndex} className="space-y-3">
                            {column.map(({ netuid, name }) => (
                              <button
                                key={netuid}
                                onClick={() => handleSubnetClick(netuid)}
                                className="w-full px-5 py-2.5 text-left text-sm hover:bg-white/20 transition-colors truncate rounded-lg"
                                title={`${netuid}: ${name}`}
                              >
                                {netuid}: {name}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* TensorPulse Mindshare Link */}
                  <Link
                    to="/tensorpulse-mindshare"
                    className={`flex items-center space-x-1 bg-black/20 hover:bg-black/30 text-white px-3 md:px-4 py-2 rounded-lg text-sm md:text-base font-medium transition-colors ${
                      location.pathname === '/tensorpulse-mindshare' ? 'bg-white/10' : ''
                    }`}
                  >
                    <span>Mindshare</span>
                  </Link>

                  {/* KOLScan Link */}
                  <Link
                    to="/kolscan"
                    className={`flex items-center space-x-1 bg-black/20 hover:bg-black/30 text-white px-3 md:px-4 py-2 rounded-lg text-sm md:text-base font-medium transition-colors ${
                      location.pathname === '/kolscan' ? 'bg-white/10' : ''
                    }`}
                  >
                    <Users className="w-4 h-4 text-cyan-400 mr-1" />
                    <span>KOLScan</span>
                  </Link>

                  {/* Admin dropdown - only visible to admin addresses */}
                  {isAdmin && (
                    <div className="relative group">
                      <button className="flex items-center space-x-1 bg-black/20 hover:bg-black/30 text-white px-3 md:px-4 py-2 rounded-lg text-sm md:text-base font-medium transition-colors">
                        <span>Admin</span>
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <div className="absolute right-0 mt-2 w-48 py-2 bg-black/90 backdrop-blur-xl rounded-lg border border-white/20 shadow-xl shadow-black/50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                        {adminPages.map(({ path, label, icon: Icon }) => (
                          <Link
                            key={path}
                            to={path}
                            className={`flex items-center space-x-2 px-4 py-2 text-sm hover:bg-white/20 transition-colors ${
                              location.pathname === path ? 'bg-white/10' : ''
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{label}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {isConnected && userData ? (
                    <ProfileButton
                      username={userData.username}
                      address={address!}
                      stakedAmount={userData.stakedAmount}
                      totalPoints={userData.totalPoints}
                      rank={userData.rank}
                      onDisconnect={disconnect}
                    />
                  ) : (
                    <button
                      onClick={connect}
                      disabled={isCheckingUser}
                      className="bg-white/10 hover:bg-white/20 text-white px-3 md:px-4 py-2 rounded-lg text-sm md:text-base font-medium transition-colors whitespace-nowrap flex items-center gap-2"
                    >
                      <span>Connect Wallet</span>
                      {isCheckingUser && (
                        <Loader className="w-4 h-4 animate-spin" />
                      )}
                    </button>
                  )}

                  <span className="text-[10px] text-gray-400 hidden md:inline-block">Beta v1.0</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile Wallet Banner */}
        <div className="relative z-40 bg-black/60 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-2">
            <p className="text-center text-sm font-medium">
              <a
                href="https://tensorpulse.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center hover:text-cyan-400 transition-colors"
              >
                Trade subnets on your mobile device! - Try TensorPulse Mobile wallet today!
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            </p>
          </div>
        </div>
      </header>

      <main className="px-4 md:px-8 pb-16 mt-8">
        <div className="max-w-[1600px] mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}